/* eslint-disable no-param-reassign */
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { Fragment, forwardRef, useEffect, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect, useWindowSize } from 'react-use';
import { Link } from '../../atoms/link/Link';
import { BreadcrumbsSchema } from '../breadcrumbsSchema';
import './Breadcrumbs.global.scss';
import styles from './Breadcrumbs.module.scss';

interface Crumb {
  id: string;
  name: string;
  reference: {
    target: string;
  };
}

interface BreadcrumbsProps {
  crumbs: Crumb[];
  className?: string;
  cropThreshold?: number;
  crumbMaxWidth?: number;
  marginBottom?: number;
  absoluteMode?: boolean;
  defaultLink?: boolean;
  [key: string]: any;
}

const Breadcrumbs = forwardRef<HTMLDivElement, BreadcrumbsProps>(
  ({ crumbs, className, cropThreshold, crumbMaxWidth, marginBottom, absoluteMode, defaultLink, ...rest }, ref) => {
    const wrapperRef = useRef<HTMLDivElement>(null);

    const [currentPointer, setCurrentPointer] = useState(0);
    const [isMounted, setIsMounted] = useState(false);

    const { width } = useWindowSize();

    useIsomorphicLayoutEffect(() => {
      setIsMounted(true);
    });

    useEffect(() => {
      if (!isMounted || !wrapperRef.current) return;

      const wrapperWidth = wrapperRef.current.getBoundingClientRect().width;

      if (wrapperWidth >= width * cropThreshold) {
        return setCurrentPointer((p) => ++p);
      }

      if (wrapperWidth + 150 <= width * cropThreshold && currentPointer > 0) {
        return setCurrentPointer((p) => --p);
      }
    }, [width, wrapperRef, isMounted, cropThreshold]);

    return (
      <>
        <div
          aria-label="breadcrumbs"
          className={clsx(styles.root, className, {
            [styles.absoluteMode]: absoluteMode,
          })}
          style={
            {
              '--breadcrumbs-mb': `${marginBottom || 10}px`,
            } as React.CSSProperties
          }
          ref={wrapperRef}
          {...rest}
        >
          <div
            className={styles.wrapper}
            style={
              {
                '--crumb-max-width': `${crumbMaxWidth}px`,
              } as React.CSSProperties
            }
          >
            {crumbs &&
              crumbs.map((crumb, i) => (
                <Fragment key={`${crumb.id}_${i}`}>
                  {crumbs.length - 1 === i ? (
                    <span ref={ref} className={`${styles.current} ${i === 0 && styles.firstItem}`}>
                      {crumb.name}
                    </span>
                  ) : (
                    <>
                      {defaultLink ? (
                        <a
                          key={`${crumb}_${i}`}
                          href={crumb.reference.target}
                          aria-label={crumb.name}
                          className={`${styles.link} ${i === 0 && styles.firstItem}`}
                        >
                          {crumb.name}
                        </a>
                      ) : (
                        <Link
                          ref={ref}
                          key={crumb.id}
                          link={crumb}
                          ariaLabel={crumb.name}
                          className={`${styles.link} ${i === 0 && styles.firstItem}`}
                          children={null}
                        />
                      )}
                      <span className={styles.separator}>/</span>
                    </>
                  )}
                </Fragment>
              ))}
          </div>
        </div>
        <BreadcrumbsSchema crumbs={crumbs} />
      </>
    );
  },
);

export default Breadcrumbs;
